import { alpha, createTheme, type Theme, type PaletteMode } from '@mui/material/styles';

/**
 * OBZR Merchant Portal — Material Design 3 theme (MUI v7).
 *
 * Adopted from the OBZR design system (`product-catalog/apps/design-system`,
 * `tokens/theme.ts`) — the codebase-accurate design language for this portal.
 * Implements the design-system token set as an MUI theme:
 *   • OBZR teal primary, bronze secondary (M3 "tertiary"), full status set
 *   • light + dark schemes
 *   • M3 type scale mapped onto MUI variants (Plus Jakarta Sans + Lexend, JetBrains Mono for data)
 *   • flat / border-led "calm enterprise" component overrides (tonal over shadow)
 *   • createObzrTheme(mode, brandSource?) — multi-tenant brand override
 *
 * Backwards-compatible with the previous theme: palette.primary, palette.secondary,
 * status colors and background/text/divider keep the same roles, so existing screens
 * are unaffected beyond the M3 radius/typography refinements.
 */

const FONT_DISPLAY =
  "'Plus Jakarta Sans', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_BODY = "'Lexend', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
export const FONT_MONO = "'JetBrains Mono', 'SFMono-Regular', ui-monospace, monospace";

/** Extended M3 container tokens that are not first-class MUI palette roles.
 *  Import where you need primaryContainer / surfaceContainer etc. */
export const obzrTokens = {
  light: {
    primaryContainer: '#CDEEE8',
    onPrimaryContainer: '#06372F',
    secondaryContainer: '#F8DCC8',
    onSecondaryContainer: '#45230C', // bronze container (M3 tertiary)
    successContainer: '#CDEBD8',
    onSuccessContainer: '#0C3A22',
    warningContainer: '#F7E6C4',
    onWarningContainer: '#4A330A',
    infoContainer: '#D6E6F5',
    onInfoContainer: '#0E2B47',
    errorContainer: '#FBDCDC',
    onErrorContainer: '#5A1213',
    surfaceContainerLowest: '#FFFFFF',
    surfaceContainerLow: '#F4F6F6',
    surfaceContainer: '#EEF1F1',
    surfaceContainerHigh: '#E8ECEC',
    surfaceContainerHighest: '#E2E7E6',
    outline: '#6F7B78',
    outlineVariant: '#C3CCC8',
    sidebarSurface: '#0F1A18',
    sidebarOnSurface: '#D6E4E0',
    sidebarOnSurfaceVariant: '#8A9A95',
    sidebarActiveContainer: 'rgba(95, 217, 200, 0.16)',
    sidebarOnActive: '#9FEFE0',
  },
  dark: {
    primaryContainer: '#0B574F',
    onPrimaryContainer: '#B4ECE2',
    secondaryContainer: '#5F3A1C',
    onSecondaryContainer: '#F8DCC8',
    successContainer: '#0C3A22',
    onSuccessContainer: '#CDEBD8',
    warningContainer: '#4A330A',
    onWarningContainer: '#F7E6C4',
    infoContainer: '#143A5E',
    onInfoContainer: '#D6E6F5',
    errorContainer: '#93000A',
    onErrorContainer: '#FFDAD6',
    surfaceContainerLowest: '#0A0F0E',
    surfaceContainerLow: '#171D1B',
    surfaceContainer: '#1B211F',
    surfaceContainerHigh: '#252B29',
    surfaceContainerHighest: '#303634',
    outline: '#899390',
    outlineVariant: '#3F4946',
    sidebarSurface: '#0A0F0E',
    sidebarOnSurface: '#D6E4E0',
    sidebarOnSurfaceVariant: '#8A9A95',
    sidebarActiveContainer: 'rgba(87, 219, 201, 0.18)',
    sidebarOnActive: '#B4ECE2',
  },
} as const;

export const elevation = {
  level0: 'none',
  level1: '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)',
  level2: '0 2px 6px rgba(16,24,40,0.08)',
  level3: '0 8px 28px rgba(16,24,40,0.12)',
  level4: '0 12px 32px rgba(16,24,40,0.14)',
  level5: '0 24px 48px rgba(16,24,40,0.18)',
  brandTile: '0 6px 16px rgba(18,124,113,0.32)',
} as const;

const lightPalette = {
  mode: 'light' as PaletteMode,
  primary: { main: '#127C71', light: '#43A595', dark: '#0B574F', contrastText: '#FFFFFF' },
  secondary: { main: '#C2703D', light: '#D89368', dark: '#94512A', contrastText: '#FFFFFF' }, // bronze = M3 tertiary
  error: { main: '#D14343', contrastText: '#FFFFFF' },
  warning: { main: '#B7791F', contrastText: '#FFFFFF' },
  info: { main: '#2F6FB0', contrastText: '#FFFFFF' },
  success: { main: '#2E7D52', contrastText: '#FFFFFF' },
  background: { default: '#F5F6F8', paper: '#FFFFFF' },
  text: { primary: '#1A2330', secondary: '#5B6675', disabled: '#9AA3B0' },
  divider: 'rgba(26, 35, 48, 0.10)',
};

const darkPalette = {
  mode: 'dark' as PaletteMode,
  primary: { main: '#57DBC9', light: '#84E8DA', dark: '#0B574F', contrastText: '#00382F' },
  secondary: { main: '#E3A879', light: '#F0C29B', dark: '#5F3A1C', contrastText: '#46230B' },
  error: { main: '#FFB4AB', contrastText: '#690007' },
  warning: { main: '#E6C36B', contrastText: '#3F2D00' },
  info: { main: '#9CC4EA', contrastText: '#0E2B47' },
  success: { main: '#86D6A6', contrastText: '#003920' },
  background: { default: '#0F1513', paper: '#171D1B' },
  text: { primary: '#DEE4E1', secondary: '#BEC9C4', disabled: '#5E6B66' },
  divider: 'rgba(222, 228, 225, 0.12)',
};

function typography() {
  return {
    fontFamily: FONT_BODY,
    // M3 Display / Headline -> MUI h1..h3 (Plus Jakarta Sans, 700–800)
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: '2.25rem',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    // M3 Title -> page/card titles
    h4: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
    },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.125rem' },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1rem' },
    subtitle1: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.43 }, // field label
    body1: { fontWeight: 400, fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontWeight: 600, letterSpacing: 0, textTransform: 'none' as const },
    caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.5, letterSpacing: '0.01em' },
    overline: {
      fontWeight: 700,
      fontSize: '0.6875rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
    },
  };
}

function components(palette: typeof lightPalette | typeof darkPalette, mode: PaletteMode) {
  const t = obzrTokens[mode];
  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: palette.background.default },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: mode === 'dark' ? 'rgba(222,228,225,0.22)' : 'rgba(26, 35, 48, 0.22)',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: mode === 'dark' ? 'rgba(222,228,225,0.38)' : 'rgba(26, 35, 48, 0.38)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
          color: palette.text.primary,
          borderBottom: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.paper,
          borderRight: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: `1px solid ${palette.divider}`, borderRadius: 16 } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 9999,
          textTransform: 'none' as const,
          paddingInline: 20,
          minHeight: 40,
        },
        sizeSmall: { minHeight: 32, paddingInline: 14 },
        outlined: { borderColor: t.outlineVariant },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
        filled: { borderRadius: 9999 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
        notchedOutline: { borderColor: t.outlineVariant },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, mode === 'dark' ? 0.18 : 0.12),
            color: mode === 'dark' ? palette.primary.main : palette.primary.dark,
            '& .MuiListItemIcon-root': {
              color: mode === 'dark' ? palette.primary.main : palette.primary.dark,
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(palette.primary.main, mode === 'dark' ? 0.24 : 0.18),
          },
        },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 38, color: palette.text.secondary } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? '#2E3633' : '#1A2330',
          fontSize: '0.75rem',
          borderRadius: 8,
          padding: '6px 10px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          boxShadow: elevation.level3,
          marginTop: 6,
        },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 28, boxShadow: elevation.level5 } } },
    MuiTable: {
      defaultProps: { size: 'small' as const },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: palette.text.secondary,
          whiteSpace: 'nowrap',
          paddingTop: 10,
          paddingBottom: 10,
        },
        root: {
          borderColor: palette.divider,
          paddingTop: 8,
          paddingBottom: 8,
        },
        sizeSmall: {
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },
  };
}

/**
 * Build an OBZR theme.
 * @param mode 'light' | 'dark'
 * @param primaryOverride optional tenant brand color (hex) — multi-tenant white-labeling.
 */
export function createObzrTheme(mode: PaletteMode = 'light', primaryOverride?: string): Theme {
  const base = mode === 'dark' ? darkPalette : lightPalette;
  const palette = primaryOverride
    ? { ...base, primary: { ...base.primary, main: primaryOverride } }
    : base;
  return createTheme({
    palette,
    shape: { borderRadius: 12 },
    typography: typography(),
    components: components(palette, mode),
  });
}

/** Default light theme (drop-in for the previous `theme` export). */
export const theme = createObzrTheme('light');
export const darkTheme = createObzrTheme('dark');
export default theme;
